import { Query } from '@cubejs-client/core';
import * as Yup from 'yup';
import {
  BlueberryFeatures,
  BlueberryFlowerStageFeatures,
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';

/**
 * This query is used in the blueberry ranch summary table and is triggered when the download button for a specific scan is clicked.
 * It compiles the estimated counts for the selected scan for each rowside and view direction and is used to generate a CSV file.
 *
 * @param {string} scanId - The scan ID to use in the query.
 * @param {FeatureEnum} serviceName - The service name to use in the query (e.g. 'blueberry'. Called modelName in database).
 * @param {string} serviceVersion - The service version (e.g. '14').
 * @return {Query} The generated query object.
 */
export const buildRowsideEstimatedCountQuery: (
  scanId: string,
  serviceName: FeatureEnum,
  serviceVersion: string
) => Query = (scanId, serviceName, serviceVersion) => ({
  dimensions: [
    CubeDimensionEnum.className,
    CubeDimensionEnum.rowsideRowNum,
    CubeDimensionEnum.rowsideViewDirection,
  ],
  measures: [CubeMeasureEnum.detectionFactEstimatedCount],
  filters: [
    {
      member: CubeDimensionEnum.detectionFactScanId,
      operator: 'equals',
      values: [scanId],
    },
    {
      member: CubeDimensionEnum.className,
      operator: 'equals',
      values: [...BlueberryFeatures, ...BlueberryFlowerStageFeatures],
    },
    {
      member: CubeDimensionEnum.modelName,
      operator: 'equals',
      values: [serviceName],
    },
    {
      member: CubeDimensionEnum.modelVersion,
      operator: 'equals',
      values: [serviceVersion],
    },
  ],
  order: [
    [CubeDimensionEnum.rowsideRowNum, 'asc'],
    [CubeDimensionEnum.rowsideViewDirection, 'asc'],
  ],
});

export const rowsideEstimatedCountCSVResponseSchema = Yup.object().shape({
  [CubeDimensionEnum.className]: Yup.string().required(),
  [CubeDimensionEnum.rowsideRowNum]: Yup.number().required(),
  [CubeDimensionEnum.rowsideViewDirection]: Yup.string().required(),
  [CubeMeasureEnum.detectionFactEstimatedCount]: Yup.number().required(),
});

export type RowsideEstimatedCountCSVResponse = Yup.InferType<
  typeof rowsideEstimatedCountCSVResponseSchema
>;
